import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { MatIcon } from '@angular/material/icon';

import { ReadonlyDisplayBase } from './readonly-display.base';
import { ValueDisplayComponent } from './value-display.component';

/**
 * Read display for an `iconpicker` answer (#21239): the stored icon token rendered as the icon
 * itself, not the raw token text ("heroicons_outline:truck").
 *
 * The picker (`icon-picker.component.ts`) stores EVERY value as a `[svgIcon]` token — a
 * namespaced one for a registered icon set (`${registerName}:${iconName}`), or a bare id with no
 * colon for a custom icon (`iconId: 'pallet'` / `'coins'`, `svg.services.ts:47-59,110`), which
 * `IconsService` registers in the DEFAULT namespace (`core/icons/icons.service.ts:23-25`). A
 * colon is never the marker for "is this a svg icon" — it is only present for a NAMESPACED one.
 * Every other consumer of this token binds `[svgIcon]` unconditionally, colon or not
 * (`icon-picker.component.ts:55,98`, the field's own preview and option list;
 * `details-header.component.ts:86`, the header's icon part) — this display matches that, with no
 * Material-ligature branch to fall out of sync with.
 *
 * `aria-label`/`title` carry the icon's own name: the glyph alone says nothing to a screen
 * reader. `aria-hidden="false"` is a literal, not a binding — `MatIcon`'s constructor reads
 * `aria-hidden` as a STATIC host attribute present at element creation
 * (`@angular/material/icon` `icon.mjs:84,97-99`) and force-sets `aria-hidden="true"` itself when
 * none is there; a bound `[attr.aria-hidden]` would lose that race (the constructor runs before
 * any binding is applied) and silently swallow the `aria-label` below it.
 *
 * Presentational: both inputs come from the row.
 */
@Component({
  selector: 'dd-icon-display',
  imports: [MatIcon, ValueDisplayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — the display must not become a box inside the value span, or the row's
  // truncate/multiline behaviour would stop at this element.
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `
    @if (icon(); as icon) {
      <mat-icon
        class="icon-size-5"
        aria-hidden="false"
        [svgIcon]="icon"
        [attr.aria-label]="icon"
        [title]="icon"></mat-icon>
    } @else {
      <dd-value-display [field]="field()" [value]="value()" />
    }
  `,
})
export class IconDisplayComponent extends ReadonlyDisplayBase {
  /** The icon token, or `null` for an empty answer — which falls through to the em-dash display. */
  readonly icon = computed(() => {
    const raw = this.value();
    return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
  });
}
